import React from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Dialog from "../../../library/Dialog";
import { generatePartnerApiKey } from "../partnerShared";
import ActionFormField from "../../../library/ActionFormField";
import useFormFeedback from "../../../library/useFormFeedback";
import { partnerHasChanges, validatePartnerForm } from "./formValidation";

export default function PartnerFormDialog({
  form,
  error,
  campaigns,
  busy,
  copied,
  onCopy,
  onFormChange,
  onClose,
  onReview,
}) {
  const editing = form?.mode === "edit";
  const errors = validatePartnerForm(form, campaigns);
  const feedback = useFormFeedback(
    errors,
    form ? `${form.mode}:${form.original?.partner_name || "new"}` : "closed"
  );
  const change = (name, value) =>
    onFormChange((current) => ({ ...current, [name]: value }));

  return (
    <Dialog
      open={Boolean(form)}
      onClose={onClose}
      title={editing ? `Edit ${form.partner_name}` : "Create partner"}
      subtitle='Set the discount and validity period for this partner’s promo codes.'
      icon={<HandshakeOutlinedIcon />}
      loading={busy}
      actions={
        <Button
          variant='contained'
          disabled={busy || !partnerHasChanges(form)}
          onClick={() => feedback.review(onReview)}
        >
          {editing ? "Review changes" : "Review and create"}
        </Button>
      }
    >
      <Stack spacing={2.5}>
        {error && <Alert severity='error'>{error}</Alert>}
        <ActionFormField
          {...feedback.field("partner_name")}
          label='Partner name'
          placeholder='e.g. lettaga'
          value={form?.partner_name || ""}
          disabled={busy || editing}
          onChange={(event) =>
            change("partner_name", event.target.value.toLowerCase())
          }
          helperText={
            editing
              ? "The partner name cannot be changed after creation."
              : "Use a short name with no spaces. Hyphens and underscores are allowed."
          }
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <ActionFormField
            {...feedback.field("discount_value")}
            label='Discount percentage'
            placeholder='e.g. 20'
            value={form?.discount_value ?? ""}
            disabled={busy}
            inputProps={{ inputMode: "numeric" }}
            onChange={(event) => change("discount_value", event.target.value)}
            helperText='A whole number from 0 to 100. For example, 20 means 20% off.'
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
            }}
          />
          <ActionFormField
            {...feedback.field("expiry_days")}
            label='Valid for'
            placeholder='e.g. 90'
            value={form?.expiry_days ?? ""}
            disabled={busy}
            inputProps={{ inputMode: "numeric" }}
            onChange={(event) => change("expiry_days", event.target.value)}
            helperText='Number of days from when a code is created. Use 1 to 3650 days.'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>days</InputAdornment>
              ),
            }}
          />
        </Stack>
        <Box>
          <FormControlLabel
            sx={{ m: 0, width: "100%", justifyContent: "space-between" }}
            labelPlacement='start'
            control={
              <Switch
                checked={Boolean(form?.is_active)}
                disabled={busy}
                onChange={(event) => change("is_active", event.target.checked)}
              />
            }
            label={
              <Typography variant='body2' fontWeight={600}>
                Allow this partner to issue new codes
              </Typography>
            }
          />
          <Typography variant='caption' color='text.secondary'>
            {form?.is_active
              ? "On: the partner can request new promo codes."
              : "Off: new signups continue without a promo code."}
          </Typography>
        </Box>
        <Box sx={{ pt: 2.5, borderTop: 1, borderColor: "divider" }}>
          <ActionFormField
            {...feedback.field("api_key")}
            label='Connection key (API key)'
            value={form?.api_key || ""}
            placeholder={
              editing && form?.original?.has_api_key
                ? `Current key ends in ${form.original.api_key_hint || "••••"}`
                : "Generate a key to connect this partner"
            }
            disabled={busy}
            helperText={
              editing && form?.keyIsNew
                ? "This new key replaces the current key when you save. Give the new key to the partner's technical contact."
                : "Give this key to the partner's technical contact to connect their system. Copy a new key before closing this dialog."
            }
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              },
              endAdornment: form?.api_key ? (
                <InputAdornment position='end'>
                  <Tooltip title={copied === "form" ? "Copied" : "Copy key"}>
                    <IconButton
                      aria-label='Copy connection key'
                      edge='end'
                      disabled={busy}
                      onClick={() => onCopy(form.api_key)}
                    >
                      {copied === "form" ? (
                        <CheckRoundedIcon color='success' />
                      ) : (
                        <ContentCopyRoundedIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
            action={
              <Button
                variant='outlined'
                disabled={busy}
                sx={{ minHeight: 40, flexShrink: 0, whiteSpace: "nowrap" }}
                onClick={() =>
                  onFormChange((current) => ({
                    ...current,
                    api_key: generatePartnerApiKey(),
                    keyIsNew: true,
                  }))
                }
              >
                Generate new key
              </Button>
            }
          />
        </Box>
        {editing && (
          <Stack
            spacing={1}
            sx={{ pt: 2.5, borderTop: 1, borderColor: "divider" }}
          >
            <Typography variant='body2' fontWeight={600}>
              Apply to existing codes (optional)
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Leave these off to keep existing codes and subscription discounts
              unchanged.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.applyDiscount)}
                  disabled={busy}
                  onChange={(event) =>
                    change("applyDiscount", event.target.checked)
                  }
                />
              }
              label={
                <Typography variant='body2'>
                  Update the discount on existing codes and subscriptions
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.applyExpiry)}
                  disabled={busy}
                  onChange={(event) =>
                    change("applyExpiry", event.target.checked)
                  }
                />
              }
              label={
                <Typography variant='body2'>
                  Update expiry dates for unused codes
                </Typography>
              }
            />
            {form.applyExpiry && (
              <Typography variant='caption' color='text.secondary'>
                The new expiry is counted from each code's original creation
                date, not from today.
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
